import router, { addDynamicRoutes } from '@/router';
import ApiService from '@/service/ApiService';
import { useNotificationStore } from '@/store/notification-store';
import { useRouterStore } from '@/store/router-store';
import { defineStore } from 'pinia';

export const useAuthStore = defineStore('auth', {
    state: () => ({
        accessToken: null,
        refreshToken: null,
        accessTokenExpireDate: null,
        refreshTokenExpireDate: null,
        user: null,
        permissions: [],
        userId: localStorage.getItem('userId') || null,
        userName: localStorage.getItem('userName') || null,
        userLoginId: localStorage.getItem('userLoginId') || null,
        isRefreshing: false // 토큰 갱신 중 여부
    }),
    actions: {
        // Initialize from localStorage on store creation
        initializeAuth() {
            const parseDate = (date) => (date ? new Date(date) : null);
            const accessToken = localStorage.getItem('accessToken');
            const refreshToken = localStorage.getItem('refreshToken');
            const accessTokenExpireDate = parseDate(localStorage.getItem('accessTokenExpireDate'));
            const refreshTokenExpireDate = parseDate(localStorage.getItem('refreshTokenExpireDate'));
            const userId = localStorage.getItem('userId');
            const userName = localStorage.getItem('userName');
            const userLoginId = localStorage.getItem('userLoginId');

            if (accessToken && refreshToken) {
                this.setTokens(accessToken, refreshToken, accessTokenExpireDate, refreshTokenExpireDate);
            }
            if (userId) {
                this.setUserId(userId);
            }
            if (userName) {
                this.setUserName(userName);
            }
            if (userLoginId) {
                this.setUserLoginId(userLoginId);
            }
            if (accessToken) {
                ApiService.setHeader('GID-AUTH', accessToken);
            }
        },

        setTokens(accessToken, refreshToken, accessTokenExpireDate, refreshTokenExpireDate) {
            this.accessToken = accessToken;
            this.refreshToken = refreshToken;
            this.accessTokenExpireDate = accessTokenExpireDate;
            this.refreshTokenExpireDate = refreshTokenExpireDate;

            localStorage.setItem('accessToken', accessToken);
            localStorage.setItem('refreshToken', refreshToken);
            localStorage.setItem('accessTokenExpireDate', accessTokenExpireDate);
            localStorage.setItem('refreshTokenExpireDate', refreshTokenExpireDate);

            ApiService.setHeader('GID-AUTH', `${accessToken}`);
        },
        setUser(user) {
            this.user = user;
        },
        setPermissions(permissions) {
            this.permissions = permissions;
        },
        setUserId(id) {
            this.userId = id;
            localStorage.setItem('userId', id);
        },
        setUserName(name) {
            this.userName = name;
            localStorage.setItem('userName', name);
        },
        setUserLoginId(loginId) {
            this.userLoginId = loginId;
            localStorage.setItem('userLoginId', loginId);
        },
        async login(credentials) {
            try {
                const response = await ApiService.post('/api/manager/login', credentials);

                if (response.data.success) {
                    const { access_token, refresh_token } = response.data.data;

                    if (access_token && refresh_token) {
                        this.setTokens(access_token.value, refresh_token.value, access_token.expire_date, refresh_token.expire_date);
                        ApiService.setHeader('GID-AUTH', access_token.value);
                        this.setUserId(response.data.data.manager_id);
                        this.setUserName(response.data.data.manager_name);
                        this.setUserLoginId(response.data.data.login_id);
                        const routerStore = useRouterStore();
                        try {
                            await routerStore.fetchRouterItems();
                            await addDynamicRoutes(router, routerStore.routerList);
                            const notificationStore = useNotificationStore();
                            await notificationStore.initializeWebSocket();
                            //routerStore.saveRouterListToStorage();
                            // 라우터가 준비될 때까지 대기
                        } catch (error) {
                            console.error('앱 초기화 실패:', error);
                        }
                        await router.isReady();

                        //credentials.id
                        // if (user) {
                        //     this.setUser(user);
                        // }
                        // await this.getPermissions();
                    }
                } else {
                    throw new Error(response.data.error || 'Login failed');
                }
            } catch (error) {
                // console.error('Login error:', error.response?.data?.msg || error.message);
                throw error;
            }
        },
        async getPermissions() {
            try {
                const response = await ApiService.get('/api/authorization/manager');
                this.setPermissions(response.data);
            } catch (error) {
                console.error('Fetch permissions error:', error);
                throw error;
            }
        },
        logout() {
            this.accessToken = null;
            this.refreshToken = null;
            this.accessTokenExpireDate = null;
            this.refreshTokenExpireDate = null;
            this.user = null;
            this.permissions = [];
            this.userId = null;
            this.userName = null;
            this.userLoginId = null;

            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('accessTokenExpireDate');
            localStorage.removeItem('refreshTokenExpireDate');
            localStorage.removeItem('userId');
            localStorage.removeItem('userName');
            localStorage.removeItem('userLoginId');
            localStorage.removeItem('routerLastFetched');
            ApiService.removeHeader('GID-AUTH');

            const routerStore = useRouterStore();
            routerStore.setRouterList([]);
            routerStore.setInitialized(false);
            routerStore.saveRouterListToStorage();
            addDynamicRoutes(router, routerStore.routerList);
            const notificationStore = useNotificationStore();
            notificationStore.disconnectWebSocket();
            notificationStore.resetConnectionAttempts();
            router.push('/login');
        },
        hasPermission(permission) {
            return this.permissions.includes(permission);
        },
        checkAuth() {
            const accessToken = localStorage.getItem('accessToken');
            const refreshToken = localStorage.getItem('refreshToken');
            const accessTokenExpireDate = localStorage.getItem('accessTokenExpireDate');
            const refreshTokenExpireDate = localStorage.getItem('refreshTokenExpireDate');

            if (!accessToken || !refreshToken) {
                //this.logout();
                return false; // 토큰이 없으면 인증 실패
            }

            this.setTokens(accessToken, refreshToken, accessTokenExpireDate, refreshTokenExpireDate);

            if (this.isAccessTokenExpired()) {
                console.log('Access token expired.');

                if (this.isRefreshTokenExpired()) {
                    this.logout();
                    return false;
                }

                return this.refreshTokens();
            }

            return true;
        },
        isTokenExpired(expireDate) {
            if (!expireDate) return true; // 만료일이 없으면 만료된 것으로 처리
            return new Date(expireDate) < new Date();
        },
        isAccessTokenExpired() {
            return this.isTokenExpired(this.accessTokenExpireDate);
        },
        isRefreshTokenExpired() {
            return this.isTokenExpired(this.refreshTokenExpireDate);
        },
        async refreshTokens() {
            if (this.isRefreshing) {
                return false; // Prevent concurrent refresh token requests
            }
            this.isRefreshing = true;

            try {
                const response = await ApiService.post('/api/manager/token', { refresh_token: this.refreshToken });
                if (response.data.success) {
                    const { access_token, refresh_token } = response.data.data;
                    this.setTokens(access_token.value, refresh_token.value, access_token.expire_date, localStorage.getItem('refreshTokenExpireDate')); // , refresh_token.expire_date
                    ApiService.setHeader('GID-AUTH', access_token.value);
                    console.info('토큰 갱신');
                    return true; // 토큰 갱신 성공
                } else {
                    this.logout(); // Refresh token 만료 또는 갱신 실패 시 로그아웃
                    return false;
                }
            } catch (error) {
                console.error('토큰 갱신 오류:', error);
                this.logout(); // 갱신 중 오류 발생 시 로그아웃
                return false;
            } finally {
                this.isRefreshing = false;
            }
        }
    },
    getters: {
        getUserId: (state) => state.userId,
        getUserName: (state) => state.userName,
        getUserLoginId: (state) => state.userLoginId
    }
});
